import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, InputNumber, Input, Alert, Steps, Spin, message, Select } from 'antd';
import { 
    InfoCircleOutlined, 
    LoadingOutlined, 
    CheckCircleFilled, 
    WifiOutlined, 
    ScanOutlined,
    RightOutlined,
    ArrowRightOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { getEmployees } from '../../services/hr.service';
import { 
    startFingerprintEnroll, 
    getFingerprintEnrollStatus, 
    triggerEsp32Enroll 
} from '../../services/attendance.service';
import styled, { keyframes, css } from 'styled-components';

const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
    border: 1px solid rgba(0, 0, 0, 0.05);
    overflow: hidden;
  }
  .ant-modal-header {
    border-bottom: none;
    padding: 24px 24px 0px;
  }
  .ant-modal-title {
    font-size: 20px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #111827;
  }
  .ant-modal-body {
    padding: 24px;
  }
`;

const StepperWrap = styled.div`
  margin-bottom: 28px;
  .ant-steps-item-title {
    font-size: 13px !important;
    font-weight: 600 !important;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.5);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 16px rgba(79, 70, 229, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(79, 70, 229, 0);
  }
`;

const greenPulse = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 16px rgba(16, 185, 129, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
`;

const FingerPulseCircle = styled.div`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 40px;
  margin: 32px auto;
  animation: ${pulse} 2s infinite;
  transition: all 0.3s;

  ${({ $success }) => $success && css`
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    animation: ${greenPulse} 2s infinite;
  `}
`;

const MainContent = styled.div`
  min-height: 240px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const GuidanceBox = styled.div`
  background: #f9fafb;
  border: 1px dashed #e5e7eb;
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 24px;
  font-size: 13.5px;
  color: #4b5563;
  
  ul {
    margin: 8px 0 0 0;
    padding-left: 20px;
    li {
      margin-bottom: 4px;
    }
  }
`;

const IPStatusTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  color: #374151;
  font-weight: 500;
  margin-top: 4px;
`;

const StatusText = styled.h3`
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  color: #111827;
  margin-top: 16px;
  margin-bottom: 8px;
`;

const SubText = styled.p`
  font-size: 14px;
  color: #6b7280;
  text-align: center;
  margin-bottom: 24px;
  line-height: 1.5;
`;

const HardwareStepsWrap = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const MiniStepNode = styled.div`
  width: 32px;
  height: 6px;
  border-radius: 10px;
  background: #e5e7eb;
  transition: all 0.3s ease;

  ${({ $active }) => $active && css`
    background: #4f46e5;
    width: 44px;
  `}

  ${({ $completed }) => $completed && css`
    background: #10b981;
  `}
`;

const StyledButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(79, 70, 229, 0.25);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(79, 70, 229, 0.35);
  }

  &:disabled {
    background: #d1d5db;
    color: #9ca3af;
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryBtn = styled.button`
  background: transparent;
  border: 1px solid #d1d5db;
  color: #374151;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
`;

const FingerprintEnrollWizard = ({ open, onCancel, userId, userName, onSuccess }) => {
    const [currentStep, setCurrentStep] = useState(0); // 0: Setup, 1: Scanning, 2: Success
    const [esp32Ip, setEsp32Ip] = useState(() => localStorage.getItem('code360_esp32_ip') || '192.168.1.15');
    const [slotId, setSlotId] = useState(1);
    const [loading, setLoading] = useState(false);
    const [pollingMessage, setPollingMessage] = useState('');
    const [hardwareStep, setHardwareStep] = useState(0); // 0 to 4
    const [mixedContentWarning, setMixedContentWarning] = useState(false);
    const pollingIntervalRef = useRef(null);

    const { data: employeesResponse } = useQuery({
        queryKey: ['hr', 'employees', { page: 1, limit: 100 }],
        queryFn: () => getEmployees({ page: 1, limit: 100 }),
        enabled: open,
    });

    const occupiedSlots = React.useMemo(() => {
        const list = employeesResponse?.data ?? [];
        return list
            .map((e) => Number(e.thumbId))
            .filter(Boolean);
    }, [employeesResponse]);

    const availableSlots = React.useMemo(() => {
        const occupied = new Set(occupiedSlots);
        const list = [];
        for (let i = 1; i <= 162; i++) {
            if (!occupied.has(i)) {
                list.push(i);
            }
        }
        return list;
    }, [occupiedSlots]);

    // Load custom IP from storage and auto-select first free slot
    useEffect(() => {
        const stored = localStorage.getItem('code360_esp32_ip');
        if (stored) setEsp32Ip(stored);
        
        if (open && availableSlots.length > 0) {
            setSlotId(availableSlots[0]);
        }
    }, [open, availableSlots]);

    // Clean interval on modal close
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    const startPolling = () => {
        setHardwareStep(0);
        setPollingMessage('Initializing enrollment connection...');
        
        pollingIntervalRef.current = setInterval(async () => {
            try {
                const data = await getFingerprintEnrollStatus(userId);
                console.log('[Fingerprint Enrollment Polling Log]:', data);
                
                if (data.status === 'enrolling') {
                    setHardwareStep(data.step);
                    setPollingMessage(data.message || 'Place finger on the physical sensor...');
                }
                
                // Step 4 is successful completion. Step 0 is fail/idle
                if (data.step === 4 && data.status === 'failed') {
                    clearInterval(pollingIntervalRef.current);
                    message.error(data.message || 'Enrollment failed. The slot may already be in use.');
                    setCurrentStep(0);
                } else if (data.step === 4 && data.status !== 'failed') {
                    clearInterval(pollingIntervalRef.current);
                    setHardwareStep(4);
                    setPollingMessage('Fingerprint saved successfully!');
                    setCurrentStep(2);
                    onSuccess?.();
                } else if (data.status === 'idle' && data.step === 0) {
                    // Timeout/cancel
                    clearInterval(pollingIntervalRef.current);
                    message.error('Fingerprint enrollment timed out or was cancelled.');
                    setCurrentStep(0);
                }
            } catch (err) {
                console.error('Polling error:', err);
            }
        }, 1000);
    };

    const handleStartScan = async () => {
        if (!esp32Ip) {
            message.warning('Please enter the ESP32 Device Local IP.');
            return;
        }
        if (!slotId || slotId < 1 || slotId > 162) {
            message.warning('Slot ID must be between 1 and 162.');
            return;
        }

        setLoading(true);
        setMixedContentWarning(false);
        localStorage.setItem('code360_esp32_ip', esp32Ip.trim());

        try {
            // 1. Tell VPS backend to prepare session listener
            await startFingerprintEnroll({ userId, slotId });
            
            // 2. Direct browser GET call to ESP32 on LAN
            try {
                await triggerEsp32Enroll(esp32Ip, slotId);
            } catch (err) {
                console.warn('ESP32 Direct trigger failed (possibly mixed-content or blocked):', err);
                setMixedContentWarning(true);
            }

            // 3. Advance to interactive stepper and start polling
            setCurrentStep(1);
            startPolling();
        } catch (err) {
            message.error(err?.response?.data?.message || err?.message || 'Failed to start enrollment session.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }
        setCurrentStep(0);
        setHardwareStep(0);
        setPollingMessage('');
        setMixedContentWarning(false);
        onCancel();
    };

    return (
        <StyledModal
            open={open}
            onCancel={handleClose}
            title={
                <>
                    <ScanOutlined style={{ color: '#4f46e5' }} />
                    <span>Enroll Fingerprint Wizard</span>
                </>
            }
            footer={null}
            destroyOnClose
            width={480}
            centered
        >
            <StepperWrap>
                <Steps
                    size="small"
                    current={currentStep}
                    items={[
                        { title: 'Configuration' },
                        { title: 'Biometric Capture' },
                        { title: 'Completed' },
                    ]}
                />
            </StepperWrap>

            <MainContent>
                {currentStep === 0 && (
                    <div>
                        <GuidanceBox>
                            <strong>Biometric Setup Guide:</strong>
                            <ul>
                                <li>Specify a hardware storage slot between 1 and 162.</li>
                                <li>Ensure the ESP32 local server is active on the office Wi-Fi network.</li>
                                <li>Ensure browser permissions allow connections to the local address.</li>
                            </ul>
                        </GuidanceBox>

                        <Form layout="vertical">
                            <FormRow>
                                <Form.Item 
                                    label="ESP32 Local IP Address" 
                                    required
                                    help="The local network IP of your fingerprint reader."
                                >
                                    <Input
                                        prefix={<WifiOutlined style={{ color: '#aaa' }} />}
                                        placeholder="e.g. 192.168.1.15"
                                        value={esp32Ip}
                                        onChange={(e) => setEsp32Ip(e.target.value)}
                                        disabled={loading}
                                    />
                                </Form.Item>
                                 <Form.Item 
                                     label="Biometric Storage Slot ID" 
                                     required
                                     help="Available slots on device (1 - 162)"
                                 >
                                     <Select
                                         style={{ width: '100%' }}
                                         value={slotId}
                                         onChange={(val) => setSlotId(val)}
                                         disabled={loading}
                                         options={availableSlots.map(s => ({
                                             value: s,
                                             label: `Slot ${s}`
                                         }))}
                                         showSearch
                                         optionFilterProp="label"
                                     />
                                 </Form.Item>
                            </FormRow>
                            
                            <StyledButton onClick={handleStartScan} disabled={loading}>
                                {loading ? (
                                    <>
                                        <LoadingOutlined />
                                        Booting Hardware Sensor...
                                    </>
                                ) : (
                                    <>
                                        <ScanOutlined />
                                        Start Scan
                                    </>
                                )}
                            </StyledButton>
                        </Form>
                    </div>
                )}

                {currentStep === 1 && (
                    <div style={{ textAlign: 'center' }}>
                        <FingerPulseCircle>
                            <ScanOutlined />
                        </FingerPulseCircle>
                        
                        <StatusText>{pollingMessage}</StatusText>
                        <SubText>
                            Enrolling <strong>{userName}</strong> into device Slot <strong>{slotId}</strong>.
                        </SubText>

                        <HardwareStepsWrap>
                            <MiniStepNode $active={hardwareStep === 1} $completed={hardwareStep > 1} />
                            <MiniStepNode $active={hardwareStep === 2} $completed={hardwareStep > 2} />
                            <MiniStepNode $active={hardwareStep === 3} $completed={hardwareStep > 3} />
                            <MiniStepNode $active={hardwareStep === 4} $completed={hardwareStep === 4} />
                        </HardwareStepsWrap>

                        {mixedContentWarning && (
                            <Alert
                                style={{ textAlign: 'left', marginTop: 16 }}
                                message="Mixed Content Diagnostic"
                                description="Direct browser trigger to ESP32 was blocked/timed out. Polling is active; verify the sensor's physical light is blinking blue to capture."
                                type="warning"
                                showIcon
                                closable
                            />
                        )}

                        <div style={{ marginTop: 24 }}>
                            <SecondaryBtn onClick={handleClose}>
                                Cancel Enrollment
                            </SecondaryBtn>
                        </div>
                    </div>
                )}

                {currentStep === 2 && (
                    <div style={{ textAlign: 'center' }}>
                        <FingerPulseCircle $success>
                            <CheckCircleFilled />
                        </FingerPulseCircle>

                        <StatusText>Enrollment Success!</StatusText>
                        <SubText>
                            Fingerprint biometric credential for <strong>{userName}</strong> successfully mapped to Slot <strong>{slotId}</strong> on the ESP32 database.
                        </SubText>

                        <StyledButton onClick={handleClose}>
                            Done
                        </StyledButton>
                    </div>
                )}
            </MainContent>
        </StyledModal>
    );
};

export default FingerprintEnrollWizard;
